import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { mkConfig, generateCsv, download } from "export-to-csv";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useMaterialReactTable } from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import SearchableSelectFilter from "../pages/Report/components/SearchableSelectFilter";

import {
  fetchReportes,
  fetchComplementosReporte,
  ejecutarReporte,
  fetchOpcionesFiltroDependiente,
} from "../api/ReportGeneratorApi";

const formatDate = (date) => {
  if (!date) return null;
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(d.getDate()).padStart(2, "0")}`;
};

const dateCellRenderer = ({ cell }) =>
  cell.getValue() ? new Date(cell.getValue()).toLocaleDateString() : "";

export const useReportGenerator = () => {
  const [reportes, setReportes] = useState([]);
  const [reporteSeleccionado, setReporteSeleccionado] = useState(null);
  const [filtrosBackend, setFiltrosBackend] = useState([]);
  const [columnasBackend, setColumnasBackend] = useState([]);
  const [valoresFiltros, setValoresFiltros] = useState({});
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [limiteRegistros, setLimiteRegistros] = useState(1000);
  const cacheRef = useRef({ key: null, data: [] });

  /* ============================
     🔽 NUEVO ESTADO
     ============================ */
  const [disabledFiltros, setDisabledFiltros] = useState({});

  useEffect(() => {
    fetchReportes().then(setReportes).catch(() => setReportes([]));
  }, []);

  const reportesMap = useMemo(() => {
    const m = new Map();
    reportes.forEach((r) => m.set(r.reporte_id, r));
    return m;
  }, [reportes]);

  useEffect(() => {
    if (!reporteSeleccionado) return;
    fetchComplementosReporte(reporteSeleccionado.reporte_id)
      .then(({ filtros, columnas }) => {
        setFiltrosBackend(filtros);
        setColumnasBackend(columnas);
        setValoresFiltros({});
        setData([]);
        cacheRef.current = { key: null, data: [] };

        /* ============================
           🔽 INICIALIZAR DEPENDIENTES
           ============================ */
        const d = {};
        filtros.forEach((f) => {
          if (String(f.esDependiente) === "1") {
            d[f.filtro_id] = true;
            f.opciones = [];
          }
        });
        setDisabledFiltros(d);
      })
      .catch(() => {
        setFiltrosBackend([]);
        setColumnasBackend([]);
      });
  }, [reporteSeleccionado]);

  const updateFiltro = useCallback(
    async (id, valor) => {
      setValoresFiltros((p) => ({ ...p, [id]: valor }));

      /* ============================
         🔽 LIMPIAR HIJOS Y NIETOS RECURSIVAMENTE
         ============================ */
      const limpiarDescendientes = (padreId) => {
        const directos = filtrosBackend.filter(
          (f) => String(f.dependeDe) === String(padreId)
        );
        for (const h of directos) {
          setValoresFiltros((p) => ({ ...p, [h.filtro_id]: null }));
          setDisabledFiltros((p) => ({ ...p, [h.filtro_id]: true }));
          h.opciones = [];
          limpiarDescendientes(h.filtro_id);
        }
      };

      limpiarDescendientes(id);

      /* ============================
         🔽 BUSCAR Y CARGAR HIJOS DIRECTOS
         ============================ */
      const hijos = filtrosBackend.filter(
        (f) => String(f.dependeDe) === String(id)
      );

      const hasValue = Array.isArray(valor) ? valor.length > 0 : (valor !== null && valor !== "");

      if (hasValue) {
        const valorParam = Array.isArray(valor) ? valor.join(',') : valor;
        for (const h of hijos) {
          const ops = await fetchOpcionesFiltroDependiente(
            h.filtro_id,
            valorParam
          );
          h.opciones = ops;
          setDisabledFiltros((p) => ({ ...p, [h.filtro_id]: false }));
        }
      }
    },
    [filtrosBackend]
  );

  const construirFiltrosPayload = useCallback(() => {
    return filtrosBackend
      .map((f) => {
        const v = valoresFiltros[f.filtro_id];
        if (v === undefined || v === null || v === "") return null;
        if (Array.isArray(v) && v.length === 0) return null;

        if (f.tipo_dato === "select" || f.tipo_dato === "multi-select") {
          let formattedValue = v;
          if (Array.isArray(v)) {
            const isNumeric = v.every((item) => /^\d+$/.test(String(item)));
            formattedValue = isNumeric
              ? v.join(',')
              : v.map((item) => `'${String(item).replace(/'/g, "''")}'`).join(',');
          }
          return {
            idFiltro: f.filtro_id,
            valor: formattedValue,
          };
        }

        if (f.tipo_dato === "dateRange") {
          if (!v?.desde && !v?.hasta) return null;
          return {
            idFiltro: f.filtro_id,
            valor: {
              desde: v.desde ? formatDate(v.desde) : null,
              hasta: v.hasta ? formatDate(v.hasta) : null,
            },
          };
        }

        if (f.tipo_dato === "date") {
          return {
            idFiltro: f.filtro_id,
            valor: formatDate(v),
          };
        }

        return {
          idFiltro: f.filtro_id,
          valor: v,
        };
      })
      .filter(Boolean);
  }, [filtrosBackend, valoresFiltros]);

  const obtenerDatosReporte = useCallback(
    async ({ forzar = false } = {}) => {
      if (!reporteSeleccionado) {
        toast.warning("Debe seleccionar un reporte");
        return null;
      }

      const filtros = construirFiltrosPayload();
      if (!filtros.length) {
        toast.warning("Debe ingresar al menos un filtro");
        return null;
      }

      const cacheKey = `${reporteSeleccionado.reporte_id}_${JSON.stringify(
        filtros
      )}_${limiteRegistros}`;

      if (!forzar && cacheRef.current.key === cacheKey && cacheRef.current.data) {
        return cacheRef.current.data;
      }

      const descLimite =
        Number(limiteRegistros) === 0
          ? "sin límite"
          : `${Number(limiteRegistros).toLocaleString()} registros`;
      toast.info(`Consultando reporte (${descLimite})...`);

      setLoading(true);
      try {
        const json = await ejecutarReporte(
          reporteSeleccionado.reporte_id,
          filtros,
          limiteRegistros
        );
        const rows = Array.isArray(json?.data) ? json.data : [];
        cacheRef.current = { key: cacheKey, data: rows };
        return rows;
      } catch {
        toast.error("Error al consultar el reporte");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [reporteSeleccionado, construirFiltrosPayload, limiteRegistros]
  );

  const columns = useMemo(
    () =>
      columnasBackend.map((c) => {
        const tipo = c.tipoDato?.toLowerCase() || c.tipo_dato?.toLowerCase() || "";
        const isDate = tipo.includes("date");
        const isNumeric =
          tipo.includes("numeric") ||
          tipo.includes("int") ||
          tipo.includes("float") ||
          tipo.includes("decimal") ||
          tipo.includes("money") ||
          tipo.includes("number");

        let filterVariant = "text";
        let filterFn = "contains";
        if (isDate) {
          filterVariant = "date-range"; // Rango de fechas es ideal para reportes
          filterFn = "betweenInclusive";
        } else if (isNumeric) {
          filterVariant = "range"; // 2 inputs [Min, Max] para números
          filterFn = "betweenInclusive";
        }

        const baseSize = Math.max(c.label.length * 9, 110);
        // Las columnas de rango (2 inputs) necesitan más espacio horizontal mínimo
        const size = (filterVariant === "range" || filterVariant === "date-range") ? Math.max(baseSize, 140) : baseSize;

        return {
          accessorKey: c.campo,
          header: c.label,
          size,
          filterVariant,
          filterFn,
          muiFilterTextFieldProps: ({ rangePosition }) => ({
            placeholder: filterVariant === "range" || filterVariant === "date-range"
              ? (rangePosition === "min" ? "Min" : "Max")
              : "Buscar...",
            sx: {
              minWidth: "40px",
              "& .MuiInputBase-input": {
                padding: "2px 4px !important",
                fontSize: "var(--app-font-size) !important"
              }
            }
          }),
          Cell: isDate ? dateCellRenderer : undefined,
        };
      }),
    [columnasBackend]
  );

  const consultar = useCallback(async () => {
    const rows = await obtenerDatosReporte();
    if (rows !== null) {
      setData(rows);
      if (rows.length === 0) {
        toast.info("La consulta no arrojó resultados");
      }
    }
  }, [obtenerDatosReporte]);

  const descargarCsv = useCallback(
    (rows) => {
      if (!rows || !rows.length) {
        toast.warning("No hay registros para exportar");
        return;
      }
      const nombreArchivo = (reporteSeleccionado?.nombre || "reporte")
        .toLowerCase()
        .replace(/\s+/g, "_");
      const cfg = mkConfig({
        useKeysAsHeaders: true,
        filename: nombreArchivo,
      });
      download(cfg)(generateCsv(cfg)(rows));
    },
    [reporteSeleccionado]
  );

  const descargarExcel = useCallback(
    (rows) => {
      if (!rows || !rows.length) {
        toast.warning("No hay registros para exportar");
        return;
      }
      const nombreArchivo = (reporteSeleccionado?.nombre || "reporte")
        .toLowerCase()
        .replace(/\s+/g, "_");
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Reporte");
      XLSX.writeFile(wb, `${nombreArchivo}.xlsx`);
    },
    [reporteSeleccionado]
  );

  const descargarPdf = useCallback(
    (rows) => {
      if (!rows || !rows.length) {
        toast.warning("No hay registros para exportar");
        return;
      }
      const nombreArchivo = (reporteSeleccionado?.nombre || "reporte")
        .toLowerCase()
        .replace(/\s+/g, "_");
      const doc = new jsPDF({ orientation: "landscape" });
      autoTable(doc, {
        head: [columns.map((c) => c.header)],
        body: rows.map((r) => columns.map((c) => r[c.accessorKey] ?? "")),
      });
      doc.save(`${nombreArchivo}.pdf`);
    },
    [columns, reporteSeleccionado]
  );

  const exportarDirecto = useCallback(
    async (tipo) => {
      const rows = await obtenerDatosReporte();
      if (!rows) return;
      if (tipo === "csv") descargarCsv(rows);
      else if (tipo === "excel") descargarExcel(rows);
      else if (tipo === "pdf") descargarPdf(rows);
    },
    [obtenerDatosReporte, descargarCsv, descargarExcel, descargarPdf]
  );

  const table = useMaterialReactTable({
    columns,
    data,
    layoutMode: "semantic",
    enablePagination: true,
    enableStickyHeader: true,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    localization: MRT_Localization_ES,
    state: { isLoading: loading },
    initialState: {
      density: "compact",
      pagination: { pageSize: 50 },
      showGlobalFilter: false,
      showColumnFilters: false,
    },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        boxShadow: "none",
        overflow: "hidden",
        fontSize: 'var(--app-font-size)',
      },
    },
    muiTableContainerProps: {
      sx: {
        flex: 1,
        minHeight: 0,
        maxHeight: "100%",
        overflow: "auto",
      },
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: "#f5f5f5",
        fontWeight: "bold",
        fontSize: 'var(--app-font-size)',
        position: "sticky",
        top: 0,
        zIndex: 2,
      },
    },
    muiTableBodyCellProps: {
      sx: {
        fontSize: 'var(--app-font-size)',
        padding: '6px 8px', // 👈 Reducido
      },
    },
    muiTopToolbarProps: { // 👈 Actualizado con selectores más específicos
      sx: {
        minHeight: '36px !important', // 👈 !important para forzar
        height: '36px !important', // 👈 !important para forzar
        padding: '2px 8px !important', // 👈 Reducido
        backgroundColor: '#fafafa', // 👈 Opcional: fondo sutil
        borderBottom: '1px solid #e0e0e0', // 👈 Opcional: separador

        // 👇 Contenedor principal del toolbar
        '& > .MuiBox-root': {
          minHeight: '36px !important',
          height: '36px !important',
          padding: '0 !important',
          gap: '2px !important',
        },

        // 👇 Collapse de alertas (cuando aparecen mensajes)
        '& .MuiCollapse-root': {
          minHeight: '0 !important',
          '& .MuiAlert-root': {
            padding: '2px 8px',
            minHeight: '28px',
            fontSize: 'var(--app-font-size)',
          },
        },

        // 👇 Zona de drop para agrupar
        '& .Mui-ToolbarDropZone': {
          minHeight: '28px !important',
          height: '28px !important',
          padding: '2px 8px !important',
          '& .MuiTypography-root': {
            fontSize: 'var(--app-font-size)',
          },
        },

        // 👇 Contenedor de botones
        '& .MuiBox-root.css-zrlv9q, & > div:last-child': {
          minHeight: '36px !important',
          height: '36px !important',
          display: 'flex',
          alignItems: 'center',
          gap: '2px !important',
        },

        // 👇 Botones del toolbar
        '& .MuiIconButton-root': {
          padding: '4px !important',
          width: '28px',
          height: '28px',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        },

        // 👇 Iconos dentro de los botones
        '& .MuiSvgIcon-root': {
          fontSize: '18px !important',
          width: '18px',
          height: '18px',
        },

        // 👇 Campo de búsqueda cuando está visible
        '& .MuiTextField-root': {
          margin: '0 !important',
          '& .MuiInputBase-root': {
            height: '28px !important',
            fontSize: 'var(--app-font-size)',
            padding: '0 8px',
          },
          '& .MuiInputBase-input': {
            padding: '4px 0',
            fontSize: 'var(--app-font-size)',
          },
        },
      },
    },
    muiBottomToolbarProps: {
      sx: {
        minHeight: '38px !important',
        height: '38px !important',
        boxShadow: "none",
        borderTop: "1px solid #e0e0e0",
        position: "relative",
        zIndex: 1,
        padding: '2px 8px !important',
        fontSize: 'var(--app-font-size)',
        backgroundColor: '#fafafa',

        '& .MuiTablePagination-root': {
          fontSize: 'var(--app-font-size)',
          padding: '0 !important',
        },
        '& .MuiTablePagination-toolbar': {
          minHeight: '36px !important',
          height: '36px !important',
          padding: '0 4px !important',
        },
        '& .MuiTablePagination-selectLabel': {
          fontSize: 'var(--app-font-size)',
          margin: '0 !important',
        },
        '& .MuiTablePagination-displayedRows': {
          fontSize: 'var(--app-font-size)',
          margin: '0 !important',
        },
        '& .MuiTablePagination-select': {
          fontSize: 'var(--app-font-size)',
          paddingTop: '2px !important',
          paddingBottom: '2px !important',
        },
        '& .MuiSelect-select': {
          fontSize: 'var(--app-font-size)',
          paddingTop: '2px !important',
          paddingBottom: '2px !important',
        },
        '& .MuiIconButton-root': {
          padding: '4px !important',
          fontSize: 'var(--app-font-size)',
        },
        '& .MuiTablePagination-actions': {
          marginLeft: '8px',
        },
      },
    },
    muiPaginationProps: {
      rowsPerPageOptions: [10, 25, 50, 100],
      sx: {
        overflow: "visible",
        fontSize: 'var(--app-font-size)',
      },
      slotProps: {
        select: {
          sx: {
            fontSize: 'var(--app-font-size)',
            '& .MuiMenuItem-root': {
              fontSize: 'var(--app-font-size)',
              minHeight: '30px',
            },
          },
        },
      },
    },
    muiTableProps: {
      sx: {
        fontSize: 'var(--app-font-size)',
      },
    },
    meta: {
      exportCsv: () => exportarDirecto("csv"),
      exportExcel: () => exportarDirecto("excel"),
      exportPdf: () => exportarDirecto("pdf"),
    },
  });

  const renderFiltro = (f, DatePicker, TextField, Stack) => {
    const value = valoresFiltros[f.filtro_id];
    const sx = {
      minWidth: 140,
      fontSize: 'var(--app-font-size)', // 👈 Agregar
      "& .MuiInputBase-root": {
        height: 'calc(var(--app-font-size) * 2.5)', // 👈 Escalar con font-size
        fontSize: 'var(--app-font-size)', // 👈 Agregar
      },
      "& .MuiInputLabel-root": {
        fontSize: 'var(--app-font-size)', // 👈 Agregar
      },
      "& .MuiMenuItem-root": { // 👈 Agregar para items del select
        fontSize: 'var(--app-font-size)',
      },
    };

    if (f.tipo_dato === "select" || f.tipo_dato === "multi-select") {
      return (
        <SearchableSelectFilter
          key={f.filtro_id}
          label={f.label}
          opciones={f.opciones || []}
          value={value ?? []}
          disabled={disabledFiltros[f.filtro_id]}
          onChange={(newVal) => updateFiltro(f.filtro_id, newVal)}
          sx={sx}
        />
      );
    }

    if (f.tipo_dato === "text" || f.tipo_dato === "number") {
      return (
        <TextField
          key={f.filtro_id}
          size="small"
          type={f.tipo_dato === "number" ? "number" : "text"}
          label={f.label}
          value={value ?? ""}
          onChange={(e) => updateFiltro(f.filtro_id, e.target.value)}
          sx={sx}
        />
      );
    }

    if (f.tipo_dato === "date") {
      return (
        <DatePicker
          key={f.filtro_id}
          label={f.label}
          value={value || null}
          onChange={(v) => updateFiltro(f.filtro_id, v)}
          slotProps={{
            textField: {
              size: "small",
              sx: {
                ...sx,
                '& .MuiInputBase-input': { // 👈 Agregar para el input del datepicker
                  fontSize: 'var(--app-font-size)',
                },
              }
            }
          }}
        />
      );
    }

    if (f.tipo_dato === "dateRange") {
      return (
        <Stack key={f.filtro_id} direction="row" spacing={0.5}>
          <DatePicker
            label="Desde"
            value={value?.desde || null}
            onChange={(v) =>
              updateFiltro(f.filtro_id, { ...value, desde: v })
            }
            slotProps={{
              textField: {
                size: "small",
                sx: {
                  ...sx,
                  '& .MuiInputBase-input': { // 👈 Agregar
                    fontSize: 'var(--app-font-size)',
                  },
                }
              }
            }}
          />
          <DatePicker
            label="Hasta"
            value={value?.hasta || null}
            onChange={(v) =>
              updateFiltro(f.filtro_id, { ...value, hasta: v })
            }
            slotProps={{
              textField: {
                size: "small",
                sx: {
                  ...sx,
                  '& .MuiInputBase-input': { // 👈 Agregar
                    fontSize: 'var(--app-font-size)',
                  },
                }
              }
            }}
          />
        </Stack>
      );
    }

    return null;
  };

  return {
    reportes,
    reportesMap,
    reporteSeleccionado,
    setReporteSeleccionado,
    filtrosBackend,
    columnasBackend,
    data,
    consultar,
    exportarDirecto,
    renderFiltro,
    table,
    limiteRegistros,
    setLimiteRegistros,
    extenderResultado: limiteRegistros > 1000,
    setExtenderResultado: (val) => setLimiteRegistros(val ? 50000 : 1000),
    loading,
  };
};
